import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React from 'react';
import * as Yup from 'yup';
import { carData } from '../../../../utils/constants/carData';
import { BidHistory } from '@common/pages/Dashboard/components/BidHistory';
import CarDetails from '@common/pages/Dashboard/components/CarDetails';
import { mainContainer } from '../VehicleInfoSidebarSx';
import theme from '../../../../theme';
import SaleInformation from '../SaleInformation';
import { Gavel } from '@mui/icons-material';
import auctionLogo from '../../../../components/assets/auction.svg';
import CustomCheckbox from '../../../../components/ui/CustomCheckbox';
import { deliveryModes } from '../../../../utils/constants/deliveryModes';
import { useFormik } from 'formik';
import {
  useGetTimeoutDetailsQuery,
  useLazyGetAuctionHistoryDealerQuery,
  useLazyGetBidHistoryQuery,
  useSetDealerBidPriceMutation,
} from '../../../../toolkit/liveAuction/liveAuctionApi';
import { useDispatch, useSelector } from 'react-redux';
import { setAlertDialog } from '../../../../toolkit/alertDialog/alertDialogSlice';
import { useLiveAuctionSocketEvents } from './customHooks/useLiveAuctionSocketEvents';
import { reducerPaths } from '../../../../app/reducerPaths';
import { useCountdownTimer } from '@common/customHooks/useCountdownTimer';
import { convertFromUTCToGivenTimezone, convertUtcStringFormat } from '../../../../utils/methods/dateTime';
import { exportAuctionTimeInMins, localAuction, localAuctionTimeInMins } from '../../../../utils/constants/auction';
import { getImagesHandler } from '../../../../utils/methods/setSellMyCarForms';
import { useLocation, useNavigate, useNavigation } from 'react-router-dom';
import { routes } from '../../../../utils/constants/routes';
import { useLazyGetVehicleQuery } from '../../../../toolkit/vehicle/vehicleApi';
import { useLazyGetAgentDetailsQuery } from '../../../../toolkit/carDetails/carDetailsApi';
import PriceInput from '../../../../components/custom/CustomInputs/PriceInput';
import { resetLiveAuctionSlice } from 'src/toolkit/liveAuction/liveAuctionSlice';
import { getRGBAFromColor } from 'src/utils/methods/getCssVariables';
import ReservePriceMetCard from '@common/pages/Dashboard/components/LiveAuction/components/ReservePriceOrNearReserverPriceCard';
import { BidHistoryPopup } from '@common/pages/Dashboard/components/BidHistoryPopup';
import { useTranslation } from 'react-i18next';
import { Bidding_Through } from '@common/utils/constants/auction';
import { sidebarBox, sidebarButton, topButtonContainer } from '../UpcomingCars/UpcomingCarsDetailSx';
import TimeLeft from '@common/pages/Dashboard/components/TimeLeft';
import { isVehicleSold } from '@common/utils/methods/generalVehicleMethods';
import { USER_ROLES } from 'src/utils/constants/users/userRoles';
import CostBreakdown from "@common/components/custom/CostBreakdownSection/CostBreakdownSection";
import { paymentStatusMapping } from '@common/utils/constants/carData';
import { useLazyDownloadBOSDealerQuery } from 'src/toolkit/payments/paymentsApi';
import { getCommaFormattedNumber } from '@common/utils/methods/stringMethods';
import PreviousAuctionsButton from '@common/components/ui/PreviousAuctionsButton';

// const TimeLeftInAuction = ({ data }) => {
//   const { t } = useTranslation('timeLeft');

//   const { countDown, isInitialised } = useCountdownTimer({
//     seconds: Math.floor(data?.seconds || 0),
//   });

//   return (
//     <Box
//       sx={{
//         backgroundColor: theme.palette.primary.main,
//         color: theme.palette.text.secondary,
//         display: 'flex',
//         flexDirection: 'column',
//         justifyContent: 'center',
//         alignItems: 'center',
//         borderRadius: '0.75rem', // 12px
//         padding: '1rem', // 16px
//         textAlign: 'center',
//         minHeight: '80px',
//       }}
//     >
//       {countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0 ? (
//         <>
//           <Typography
//             fontWeight={600}
//             fontSize="1.25rem"
//             sx={{
//               color: theme.palette.text.secondary,
//               marginBottom: '0.5rem',
//             }}
//           >
//             {t('Time Left In Auction')}
//           </Typography>
//           <Typography
//             fontWeight={500}
//             fontSize="1.125rem"
//             sx={{
//               color: theme.palette.text.secondary,
//             }}
//           >
//             <span style={{ fontWeight: 700 }}>
//               {`${countDown?.hours < 10 ? `0${countDown?.hours}` : countDown?.hours}`}
//             </span>{' '}
//             {t('hours')}{' '}
//             <span style={{ fontWeight: 700 }}>
//               {`${countDown?.minutes < 10 ? `0${countDown?.minutes}` : countDown?.minutes}`}
//             </span>{' '}
//             {t('mins')}{' '}
//             <span style={{ fontWeight: 700 }}>
//               {`${countDown?.seconds < 10 ? `0${countDown?.seconds}` : countDown?.seconds}`}
//             </span>{' '}
//             {t('sec')}
//           </Typography>
//         </>
//       ) : (
//         <Typography
//           fontWeight={700}
//           fontSize="1.25rem"
//           sx={{
//             color: theme.palette.text.secondary,
//           }}
//         >
//           {isInitialised ? (
//             t("Time's up")
//           ) : (
//             "Loading..."
//           )}
//         </Typography>
//       )}
//     </Box>
//   );
// };

const BidInformation = ({ car = {}, highestBid }) => {
  const { t } = useTranslation('bidInformation');

  // static data start...
  const placeBidInitialValues = React.useMemo(() => {
    return {
      Bid_Amount: '',
      delivery_mode: deliveryModes.Pickup,
    };
  }, []);
  // static data end...

  const isMobile = useMediaQuery('(max-width:1300px)');
  const inBetween = useMediaQuery('(max-width: 1570px) and (min-width: 1300px)');

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  // custom hooks start...
  const [setDealerBidPriceApiHandler, setDealerBidPriceApiData] = useSetDealerBidPriceMutation();
  // custom hooks end...

  // place bid form schema...
  const placeBidSchema = Yup.object().shape({
    Bid_Amount: Yup.number().required(),
    delivery_mode: Yup.string().oneOf([deliveryModes.Pickup, deliveryModes.Dropoff]).required(),
  });

  const placeBidFormik = useFormik({
    initialValues: placeBidInitialValues,
    validationSchema: placeBidSchema,
    onSubmit: async (values) => {
      if (highestBid && values?.Bid_Amount <= highestBid) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t('Error!'),
            message: t('Please enter a bid greater than the current highest bid!'),
          })
        );

        return;
      }

      const formData = new FormData();

      for (let i = 0; i < Object.entries(values)?.length; i++) {
        formData.append(Object.entries(values)?.[i]?.[0], Object.entries(values)?.[i]?.[1]);
      }

      formData.append(`Vehicle_Id`, car?.Vehicle_Id);

      // call "setDealerBidPriceApiHandler" here...
      const res = await setDealerBidPriceApiHandler({
        payload: formData,
      });

      if (res?.error || ('status' in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t('Error!'),
            message: res?.error?.data?.error || res?.data?.message || t('Some error occured!'),
          })
        );
      } else if (res?.data) {
        placeBidFormik.resetForm();
      }
    },
  });
  // place bid form schema...

  return (
    // Add inside LiveAuctionCarDetail component, before <BidHistory />
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: '0.75rem',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* <Typography fontWeight={700} color={theme.palette.primary.main}>
        {t("Bid Information")}
      </Typography> */}

      {/* Current & Previous Bid */}
      {/* <Box display="flex" gap={2}>
        <Box flex={1}>
          <Typography fontSize="0.85rem" fontWeight={600} mb={0.5}
            sx={{
              color: theme.palette.text.primary
            }}
          >
            {t("Current Bid")}
          </Typography>
          <TextField
            placeholder="$ 3,000"
            fullWidth
            size="small"
            InputProps={{
              sx: { 
                input: { 
                  fontWeight: 600,
                  "&::placeholder": {
                    color: `${theme.palette.text.disabled} !important`,
                    opacity: 1,
                  },
                },
                backgroundColor: theme.palette.background.paper 
              },
            }}
          />
        </Box>

        <Box flex={1}>
          <Typography fontSize="0.85rem" fontWeight={600} mb={0.5}
            sx={{
              color: theme.palette.text.primary
            }}
          >
            {t("Previous Bid")}
          </Typography>
          <TextField
            placeholder="$ 4,000"
            fullWidth
            size="small"
            InputProps={{
              sx: { 
                input: { 
                  fontWeight: 600,
                  "&::placeholder": {
                    color: `${theme.palette.text.disabled} !important`,
                    opacity: 1,
                  },
                },
                backgroundColor: theme.palette.background.paper 
              },
            }}
          />
        </Box>
      </Box> */}

      {/* Place Your Bid */}
      <Box
        sx={{
          backgroundColor: '#F8F8F8',
          borderRadius: '0.75rem',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography align="center" fontWeight={700} color={theme.palette.primary.main}>
          {t('Place Your Bid')}
        </Typography>

        <PriceInput
          // placeholder="$ 3,000"
          placeholder={`$ ${getCommaFormattedNumber(parseInt(car?.Reserve_Price) * 0.7)}`}
          fullWidth
          size="medium"
          name={'Bid_Amount'}
          value={placeBidFormik?.values?.Bid_Amount}
          onChange={placeBidFormik.handleChange}
          sx={{
            input: {
              fontWeight: 600,
              '&::placeholder': {
                color: `${theme.palette.text.disabled} !important`,
                opacity: 1,
              },
            },
            backgroundColor: theme.palette.background.paper,
            borderRadius: '0.5rem',
          }}
        />

        {/* Success Message */}
        {/* {setDealerBidPriceApiData?.isSuccess ? (
          <Typography align="center" fontWeight={600} color="green" fontSize="0.9rem">
            {t('Bid Amount Added Successfully')}
          </Typography>
        ) : null} */}

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          startIcon={
            <img
              src={auctionLogo}
              style={{
                width: '1.5rem',
              }}
            />
          }
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.text.secondary,
            borderRadius: '0.5rem',
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
          disabled={setDealerBidPriceApiData?.isLoading}
          onClick={placeBidFormik?.handleSubmit}
        >
          {setDealerBidPriceApiData?.isLoading ? (
            <CircularProgress
              size={22}
              sx={{
                color: theme.palette.background.paper,
              }}
            />
          ) : (
            t('Place Your Bid')
          )}
        </Button>
      </Box>

      {/* <Box
        // component="fieldset"
        sx={{
          width: '100%',
          backgroundColor: theme.palette.background.default,
          borderRadius: '0.75rem',
          // padding: "1rem",
          marginBottom: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography fontWeight={700} color={theme.palette.primary.main} sx={{ marginBottom: '0.5rem' }}>
          {t('Delivery Mode')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: inBetween ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
          }}
        >
          <CustomCheckbox
            label={t('Pickup')}
            selected={placeBidFormik?.values?.delivery_mode === deliveryModes?.Pickup}
            onClick={() => {
              placeBidFormik?.setValues((values) => {
                return {
                  ...values,
                  delivery_mode: deliveryModes?.Pickup,
                };
              });
            }}
          />
          <CustomCheckbox
            label={t('Drop Off')}
            selected={placeBidFormik?.values?.delivery_mode === deliveryModes?.Dropoff}
            onClick={() => {
              placeBidFormik?.setValues((values) => {
                return {
                  ...values,
                  delivery_mode: deliveryModes?.Dropoff,
                };
              });
            }}
          />
        </Box>
      </Box> */}
    </Box>
  );
};

const AuctionWonTag = ({ 
  highestBid,
  selectedCar 
}) => {
  const auth = useSelector((store) => store[reducerPaths.auth]);

  if (!highestBid) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '1.25rem', // Rounded corners for the main card
        padding: '1.5rem', // Inner padding for the main card
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem', // Space between the two inner boxes
        width: '100%', // Take full width of parent
        boxShadow: '0rem 0.25rem 0.625rem rgba(0, 0, 0, 0.1)', // Subtle shadow
      }}
    >
      {/* Car Sold Section */}
      <Box
        sx={{
          backgroundColor: 'rgba(43, 210, 76, 0.1)', // Light green background (10% opacity of primary.green)
          border: `0.0625rem solid ${theme.palette.primary.green}`, // Green border
          borderRadius: '0.625rem', // Rounded corners
          paddingTop: '1.5rem',
          paddingBottom: '1.5rem',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body1" // Use body1 variant for text
          sx={{
            color: theme.palette.primary.green, // Green text color
            fontWeight: 600, // Make text bold
            fontSize: '1.125rem', // Custom font size for this text
          }}
        >
          {highestBid?.Dealer_Id === auth?.user_id
            ? 'You won the auction'
            : `The auction was won by: ${highestBid?.dealer_type?.toLowerCase?.()?.includes?.(USER_ROLES.dealer) ? 
            `Dealer ID ${highestBid?.Dealer_Id}` : 
            `Buyer ID ${highestBid?.Dealer_Id}`} at $${getCommaFormattedNumber(selectedCar?.winning_amount)}`}
        </Typography>
      </Box>
    </Box>
  );
};

// Main Car Detail Page component
const LiveAuctionCarDetail = (
  {
    // selectedCar,
    // setSelectedCar
  }
) => {
  // useLocation...
  const location = useLocation();
  // useLocation...

  const navigate = useNavigate();

  const Vehicle_Id = location?.pathname?.includes?.(routes.liveAuction) ? 
    location?.pathname?.split?.(`${routes.liveAuction}/`)?.[1] : 
    location?.pathname?.split?.(`${routes.myOrders}/`)?.[1];

  const isMobile = useMediaQuery('(max-width:1300px)');

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  // useSelector start...
  const liveAuction = useSelector((store) => store[reducerPaths.liveAuction]); // useSelector start...
  const auth = useSelector((store) => store[reducerPaths.auth]);
  // useSelector end...

  // useState variables start...
  const [selectedCar, setSelectedCar] = React.useState(null);
  const [viewAuctionBidHistory, setViewAuctionBidHistory] = React.useState(false);
  const [isPaymentCompleted, setIsPaymentCompleted] = React.useState(false);
  // useState variables end...

  const liveAuctionDateTime = selectedCar?.Live_Auction_DateTime
    ? convertFromUTCToGivenTimezone({
        dateStr: convertUtcStringFormat(selectedCar?.Live_Auction_DateTime),
        timezone: auth?.timezone,
      })
    : new Date();

  const liveAuctionEndDate = new Date(liveAuctionDateTime);
  liveAuctionEndDate.setMinutes(
    liveAuctionEndDate.getMinutes() +
      (selectedCar?.auction_type === localAuction ? localAuctionTimeInMins : exportAuctionTimeInMins)
  );

  const liveAuctionRemainingSeconds = Math.floor(
    liveAuctionEndDate.getTime() - new Date().getTime() > 1000
      ? (liveAuctionEndDate.getTime() - new Date().getTime()) / 1000
      : 0
  );

  // Handlers start...
  const parsedVehicle = (selectedCar) => {
    const images = [];
    const carImages = getImagesHandler({
      data: {
        vehicle: selectedCar,
      },
    });

    for (let i = 0; i < Object.entries(carImages?.images)?.length; i++) {
      for (let j = 0; j < Object.entries(carImages?.images)?.[i]?.[1]?.length; j++) {
        if (Object.entries(carImages?.images)?.[i]?.[1]?.[j]) {
          images.push(Object.entries(carImages?.images)?.[i]?.[1]?.[j]);
        }
      }
    }

    return {
      ...selectedCar,
      images,
    };
  };

  const onNewBidHandler = async () => {
    await getBidHistoryApiHandler({
      vehicleId: selectedCar?.Vehicle_Id,
    });

    await getAuctionHistoryApiHandler({
      Vehicle_Id: selectedCar?.Vehicle_Id,
    })
  };

  const onPaymentSuccessHandler = () => {
    setIsPaymentCompleted(true);
  };
  // Handlers end...

  // custom hooks start...
  const [getBidHistoryApiHandler, getBidHistoryApiData] = useLazyGetBidHistoryQuery();
  const [getVehicleApiHandler, getVehicleApiData] = useLazyGetVehicleQuery();
  const [getAgentDetailsApiHandler, getAgentDetailsApiData] = useLazyGetAgentDetailsQuery();
  const [getAuctionHistoryApiHandler, getAuctionHistoryApiData] = useLazyGetAuctionHistoryDealerQuery();
  const [downloadBOSApiHandler, downloadBOSApiData] = useLazyDownloadBOSDealerQuery();

  useLiveAuctionSocketEvents({
    Vehicle_Id: selectedCar?.Vehicle_Id,
    onNewBidHandler,
  });

  const { countDown, isInitialised } = useCountdownTimer({
    seconds: liveAuctionRemainingSeconds,
  });
  // custom hooks end...

  const bidHistoryData = getBidHistoryApiData?.data?.TopBids?.filter?.((bid) => {
    return bid?.Bidding_Through === Bidding_Through.Auction;
  })?.map?.((bid) => {
    return {
      ...(bid || {}),
      amount: bid?.Bid_Amount,
      dateTime: bid?.Bid_Date,
      id: bid?.Bid_Id,
      dealerId: bid?.Dealer_Id,
      deliveryMode: bid?.Delivery_Mode,
      Vehicle_Id: bid?.Vehicle_Id,
    };
  });

  const highestBid = (bidHistoryData || [])
    ?.map?.((bid) => {
      return {
        ...bid,
        Bid_Amount: parseInt(bid?.Bid_Amount),
      };
    })
    ?.sort?.((bidA, bidB) => {
      const bidAAmount = parseInt(bidA?.Bid_Amount);
      const bidBAmount = parseInt(bidB?.Bid_Amount);

      if (bidAAmount < bidBAmount) {
        return 1;
      }
      if (bidAAmount > bidBAmount) {
        return -1;
      }
      return 0;
    })?.[0];

  // useEffect start...
  React.useEffect(() => {
    if (!selectedCar?.Vehicle_Id) {
      return;
    }

    (async () => {
      const res = await getBidHistoryApiHandler({
        vehicleId: selectedCar?.Vehicle_Id,
      });

      if (res?.error) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.error?.data?.message || res?.error?.data?.error || `Some error occured!`,
          })
        );
      }
    })();
  }, [selectedCar?.Vehicle_Id]);

  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getVehicleApiHandler({
          Vehicle_Id,
        });

        if (res?.error) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
            })
          );
        } else {
          setSelectedCar(res?.data?.vehicle);
          setIsPaymentCompleted(paymentStatusMapping[res?.data?.payment_status]);
        }
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (isPaymentCompleted) {
      if (Vehicle_Id) {
        const intervalId = setTimeout(() => {
          (async () => {
            const res = await getVehicleApiHandler({
              Vehicle_Id,
            });

            if (isVehicleSold({ car: res?.data?.vehicle })) {
              clearInterval(intervalId);
            }

            if (res?.error) {
              dispatch(
                setAlertDialog({
                  open: true,
                  title: `Error`,
                  message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
                })
              );
            } else {
              setSelectedCar(res?.data?.vehicle);
              setIsPaymentCompleted(paymentStatusMapping[res?.data?.payment_status]);
            }
          })();
        }, 5000);
      }
    }
  }, [Vehicle_Id, isPaymentCompleted]);

  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getAgentDetailsApiHandler({
          vehicleId: Vehicle_Id,
        });

        if (res?.error) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
            })
          );
        }
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    return () => {
      dispatch(resetLiveAuctionSlice());
    };
  }, []);

  React.useEffect(() => {
    if (!Vehicle_Id) {
      return;
    }

    (async () => {
      await getAuctionHistoryApiHandler({
        Vehicle_Id,
      });
    })();
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (!Vehicle_Id) {
      return;
    }

    (async () => {
      await getAuctionHistoryApiHandler({
        Vehicle_Id
      })
    })();
  }, [Vehicle_Id]);

  // React.useEffect(() => {
  //   if (countDown?.hours === 0 && countDown?.minutes === 0 && countDown?.seconds === 0 && isInitialised) {
  //     console.log
  //     if (isVehicleSold({ car: selectedCar }) && parseInt(highestBid?.Dealer_Id) === auth?.user_id) {
  //       dispatch(
  //         setAlertDialog({
  //           open: true,
  //           title: `Success!`,
  //           message: `Congratulations! You have won the auction. Would you like to go to Your Orders page?`,
  //           onOk: () => {
  //             navigate(`${routes.dashboardNavigation}${routes.myOrders}`);
  //           }
  //         })
  //       )
  //     } else {
  //       dispatch(
  //         setAlertDialog({
  //           open: true,
  //           title: `Alert!`,
  //           message: `The auction time is over. Would you like to go to the Live Auction page?`,
  //           onOk: () => {
  //             navigate(`${routes.dashboardNavigation}${routes.liveAuction}`);
  //           }
  //         })
  //       )
  //     }
  //   }
  // }, [
  //   countDown,
  //   selectedCar
  // ]);
  // useEffect end...

  console.log(isVehicleSold({ car: selectedCar }), selectedCar, `getAuctionHistoryApiData`)

  return (
    <Grid container spacing={0} sx={mainContainer}>
      {getVehicleApiData?.isFetching ? (
        //skeleton UI
        <Grid container spacing={0} sx={{ width: '100%', height: '100%' }}>
          {/* Left sidebar skeleton */}
          <Grid
            item
            xs={12}
            sx={{
              width: isMobile ? '100%' : '23%',
            }}
          >
            <Box sx={sidebarBox}>
              {/* Top buttons skeleton */}
              <Box sx={topButtonContainer}>
                <Skeleton variant="rectangular" width="100%" height={60} sx={{ marginBottom: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
              </Box>

              {/* Auto Bid section skeleton */}
              <Box sx={{ padding: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Skeleton variant="text" width="80px" height={28} sx={{ marginBottom: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <Box sx={{ width: '47%' }}>
                    <Skeleton variant="text" width="100px" height={30} sx={{ marginBottom: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
                  </Box>
                  <Box sx={{ width: '47%' }}>
                    <Skeleton variant="text" width="100px" height={30} sx={{ marginBottom: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
                  </Box>
                </Box>
              </Box>

              {/* Delivery Mode section skeleton */}
              <Box sx={{ padding: 2 }}>
                <Skeleton variant="text" width="120px" height={30} sx={{ marginBottom: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                  <Skeleton variant="rectangular" width={80} height={30} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={80} height={30} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="text" width="100%" height={20} />
              </Box>

              {/* Save button skeleton */}
              <Box sx={{ padding: 2 }}>
                <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1, marginBottom: 2 }} />
                <Skeleton variant="text" width="100%" height={20} />
              </Box>

              {/* Sale Information skeleton */}
              <Box sx={{ padding: 2 }}>
                <Skeleton variant="text" width="150px" height={24} sx={{ marginBottom: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                  <Skeleton variant="text" width="70%" height={20} />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Main content skeleton */}
          <Grid
            sx={{
              width: isMobile ? '100%' : '75%',
              padding: '0 16px 16px 16px',
            }}
          >
            {/* Car images skeleton */}
            <Box sx={{ marginBottom: 3 }}>
              <Skeleton variant="rectangular" width="100%" height={500} sx={{ borderRadius: 2, marginBottom: 2 }} />
            </Box>

            {/* Vehicle information skeleton */}
            <Box sx={{ marginBottom: 3 }}>
              <Skeleton variant="text" width="200px" height={32} sx={{ marginBottom: 2 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 2,
                  marginBottom: 2,
                }}
              >
                {[...Array(8)].map((_, index) => (
                  <Box key={index}>
                    <Skeleton variant="text" width="80px" height={20} sx={{ marginBottom: 0.5 }} />
                    <Skeleton variant="text" width="120px" height={30} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                {[...Array(3)].map((_, index) => (
                  <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <Skeleton
                      key={index}
                      variant="circular"
                      width={60}
                      height={60}
                      sx={{ borderRadius: '80px', mx: 2 }}
                    />
                    {index !== 3 && index !== 2 && <Skeleton variant="text" width="300px" height={10} />}
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <>
          {/* Left sidebar with buttons */}
          <Grid
            item
            sx={{
              width: isMobile ? '100%' : '25%',
            }}
          >
            <Box display={'flex'} flexDirection="column" gap={2}>
              {/* <TimeLeftInAuction /> */}
              {!isPaymentCompleted && !isVehicleSold({ car: selectedCar }) ? <TimeLeft
                data={{
                  seconds: liveAuctionRemainingSeconds,
                }}
              /> : (
                isVehicleSold({ car: selectedCar }) && isPaymentCompleted && parseInt(highestBid?.Dealer_Id) === auth?.user_id ? (
                  <Button
                    sx={{
                      width: "100%",
                      background: theme.palette.primary.main,
                      color: theme.palette.background.paper,
                      "&:disabled": {
                        background: "transparent",
                        border: `1px solid ${theme.palette.border.disabled}`
                      }
                    }}
                    onClick={async () => {
                      const res = await downloadBOSApiHandler({
                        Vehicle_Id
                      });

                      console.log(res, "downloadBOSApiHandler")

                      const filename = "Bill_Of_Sale.pdf";
                      
                      const blob = res?.data;

                      // 2. Create a temporary URL for the Blob
                      const url = URL.createObjectURL(blob);

                      // 3. Create a temporary <a> element to trigger the download
                      const link = document.createElement('a');
                      link.href = url;
                      // Set the suggested filename for the download
                      link.download = filename;

                      // 4. Programmatically click the link to start the download
                      document.body.appendChild(link);
                      link.click();

                      // 5. Clean up the temporary URL and link element
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);

                      // onClickCompleteProcessingHandler();
                      // setIsPaymentModalOpen(true);
                    }}
                    disabled={!isPaymentCompleted || downloadBOSApiData?.isLoading}
                    // disabled={getCurrencyConversionApiData?.isFetching}
                    // disabled={!currency?.conversionRates}
                  >
                    Download Bill Of Sale
                  </Button>
                ) : null
              )}

              {getAuctionHistoryApiData?.data?.auctions?.length > 0 ? (
                <Box>
                  <PreviousAuctionsButton 
                    onClick={() => {
                      setViewAuctionBidHistory(true);
                    }}
                  />
                </Box>
              ) : null}

              {getVehicleApiData?.isFetching ? null : countDown?.hours === 0 &&
                countDown?.minutes === 0 &&
                countDown?.seconds === 0 &&
                isInitialised &&
                bidHistoryData?.length > 0 &&
                isVehicleSold({
                  car: selectedCar
                }) &&
                parseInt(highestBid?.Dealer_Id) === auth?.user_id ? (
                <AuctionWonTag
                  highestBid={
                    (bidHistoryData || [])
                      ?.map?.((bid) => {
                        return {
                          ...bid,
                          Bid_Amount: parseInt(bid?.Bid_Amount),
                        };
                      })
                      ?.sort?.((bidA, bidB) => {
                        const bidAAmount = parseInt(bidA?.Bid_Amount);
                        const bidBAmount = parseInt(bidB?.Bid_Amount);

                        if (bidAAmount < bidBAmount) {
                          return 1;
                        }
                        if (bidAAmount > bidBAmount) {
                          return -1;
                        }
                        return 0;
                      })?.[0]
                  }
                />
              ) : parseInt(selectedCar?.Reserve_Price) <= highestBid?.Bid_Amount ||
                parseInt(selectedCar?.Reserve_Price) <= highestBid?.Bid_Amount + 500 ? (
                <ReservePriceMetCard car={selectedCar} highestBid={highestBid} />
              ) : null}

              {/* {getAuctionHistoryApiData?.data?.auctions?.length > 0 ? <Button
                sx={{
                  background: theme.palette.primary.main,
                  color: theme.palette.background.paper,
                  fontSize: "1.25rem",
                  textTransform: "capitalize"
                }}
                onClick={() => {
                  setViewAuctionBidHistory(true);
                }}
              >
                Bid history ({getAuctionHistoryApiData?.data?.auctions?.length})
              </Button> : null} */}

              <BidHistory
                bids={bidHistoryData || []}
                selectedCar={selectedCar}
                isLoading={getBidHistoryApiData?.isLoading}
              />

              {countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0 ? (
                <BidInformation
                  car={selectedCar}
                  highestBid={
                    (bidHistoryData || [])
                      ?.map?.((bid) => {
                        return {
                          ...bid,
                          Bid_Amount: parseInt(bid?.Bid_Amount),
                        };
                      })
                      ?.sort?.((bidA, bidB) => {
                        const bidAAmount = parseInt(bidA?.Bid_Amount);
                        const bidBAmount = parseInt(bidB?.Bid_Amount);

                        if (bidAAmount < bidBAmount) {
                          return 1;
                        }
                        if (bidAAmount > bidBAmount) {
                          return -1;
                        }
                        return 0;
                      })?.[0]?.Bid_Amount
                  }
                />
              ) : null}

              {/* <FormControl
                component="fieldset"
                sx={{
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "0.75rem",
                  padding: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <Typography
                  fontWeight={700}
                  color={theme.palette.primary.main}
                  sx={{ marginBottom: "0.5rem" }}
                >
                  Delivery Mode
                </Typography>

                <Box display="flex" flexDirection="row" gap="1rem">
                  <CustomCheckbox
                    label="Pickup"
                    selected={deliveryMode === "pickup"}
                    onClick={() => setDeliveryMode("pickup")}
                  />
                  <CustomCheckbox
                    label="Drop Off"
                    selected={deliveryMode === "dropoff"}
                    onClick={() => setDeliveryMode("dropoff")}
                  />
                </Box>
              </FormControl> */}

              <CostBreakdown
                ownerRole={selectedCar?.owner_type?.toLowerCase?.()?.includes?.(USER_ROLES.seller) ? 
                  USER_ROLES.seller :
                  USER_ROLES.dealer
                }
              />

              <SaleInformation
                data={{
                  ...(getAgentDetailsApiData?.data?.[0] || {}),
                  address: selectedCar?.Location,
                  date: selectedCar?.Live_Auction_DateTime,
                }}
                car={selectedCar}
              />
            </Box>
          </Grid>
          <Grid
            item
            sx={{
              width: isMobile ? '100%' : '73%',
            }}
          >
            <CarDetails car={parsedVehicle(selectedCar)} />
          </Grid>
          {/* Main content area */}
        </>
      )}

      {getVehicleApiData?.isFetching ? (
        <Modal
          open={true}
          sx={{
            background: getRGBAFromColor({
              color: theme.palette.text.primary,
              alpha: '0.45',
            }),
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={50} />
          </Box>
        </Modal>
      ) : null}

      {viewAuctionBidHistory ? (
        <Modal
          open={true}
          onClose={() => {
            setViewAuctionBidHistory(false);
          }}
        >
          <BidHistoryPopup
            auctionHistory={getAuctionHistoryApiData?.data?.auctions?.map?.((auction, auctionIdx) => {
              return {
                ...auction,
                bids: auction?.OldBidsHistory?.map?.((bid) => {
                  return {
                    ...bid,
                    amount: bid?.Bid_Amount,
                    dateTime: bid?.Bid_Date,
                    dealerId: bid?.Dealer_Id
                  };
                }),
              };
            })}
            selectedCar={selectedCar}
            isLoading={getAuctionHistoryApiData?.isFetching}
            handleClose={() => {
              setViewAuctionBidHistory(false);
            }}
          />
        </Modal>
      ) : null}
    </Grid>
  );
};

export default LiveAuctionCarDetail;
